import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Clock, TriangleAlert } from 'lucide-react';
import {
  formatIqd,
  fromMillidinars,
  type DrawerCloseResultDto,
  type DrawerCurrentDto,
  type DrawerMovementKind,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { ListPager } from '@/features/customers/ListPager';
import { CloseDrawerDialog } from './CloseDrawerDialog';
import { DrawerFigures, signedMoney } from './DrawerFigures';
import { DrawerMovementButtons, DrawerMovementDialog } from './DrawerMovementDialog';
import { StartCashDrawerDialog } from './StartCashDrawerDialog';
import { useDrawerCurrent, useDrawerSessions, type DrawerSessionsPage } from './useCashDrawer';

/**
 * Cash drawer — the venue's ONE drawer (owner, 2026-10-12), in place of the
 * per-employee box balance and the shifts nobody opened.
 *
 * Every CASH payment by anyone, on any device, is stamped to the open
 * session when it is written. A close counts the drawer first, keeps a float
 * and drops the rest to the safe, and the next session opens at once with
 * that float — the drawer is never left closed, and forgetting to close
 * breaks nothing (the not-counted warning is the whole consequence).
 *
 * No route gate: everyone may see that the drawer is open and since when.
 * The close is shown to `bills.create` (whoever takes money at the till),
 * the history to the owner and `finance.manageShifts`; the live expected
 * figure arrives only for the owner and `reports.view`. The API decides on
 * every request; this screen only keeps controls away from people it would
 * refuse.
 */
export function CashDrawerPage() {
  const { t } = useTranslation();
  const { can, venue, employee } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  const canClose = can('bills.create');
  const seesHistory = can('finance.manageShifts');
  // Pickups, change and owner takes: the owner and safe.manage (2026-10-12).
  const canMove = can('safe.manage');
  // The switch-over is the owner's alone — no permission key reaches it.
  const isOwner = employee?.isOwner === true;

  const [closing, setClosing] = useState(false);
  const [closed, setClosed] = useState<DrawerCloseResultDto | null>(null);
  const [page, setPage] = useState(1);
  const [moving, setMoving] = useState<DrawerMovementKind | null>(null);
  const [movementNotice, setMovementNotice] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);

  // The live tick holds still while a close is counting.
  const { current, error: currentError, reload: reloadCurrent } = useDrawerCurrent(closing);
  const inUse = current?.enabled === true;
  const {
    data: sessions,
    error: sessionsError,
    reload: reloadSessions,
  } = useDrawerSessions(seesHistory && inUse, page);

  // The session being closed is gone from the read (the drawer stopped, or a
  // refresh came back without one): drop the dialog, or the tick never resumes.
  useEffect(() => {
    if (closing && current !== undefined && !current.session) setClosing(false);
  }, [closing, current]);

  function refreshAll(): void {
    void reloadCurrent();
    void reloadSessions();
  }

  return (
    <div className="flex h-full flex-col overflow-auto">
      <header className="flex flex-wrap items-center gap-3 p-4">
        <h1 className="text-xl font-semibold text-content">{t('cashDrawer.title')}</h1>
        <p className="text-xs text-content-muted">{t('cashDrawer.subtitle')}</p>
      </header>

      <div className="min-h-0 flex-1 space-y-6 px-4 pb-6">
        <section aria-labelledby="drawer-now">
          <h2 id="drawer-now" className="mb-2 text-base font-semibold text-content">
            {t('cashDrawer.current.title')}
          </h2>
          <CurrentDrawerCard
            current={current}
            error={currentError}
            currencySymbol={currencySymbol}
            canClose={canClose}
            onClose={() => setClosing(true)}
            actions={
              canMove ? (
                <DrawerMovementButtons
                  onPick={(kind) => {
                    setMovementNotice(null);
                    setMoving(kind);
                  }}
                />
              ) : undefined
            }
            onStart={isOwner ? () => setStarting(true) : undefined}
          />
          {movementNotice ? (
            <p
              role="status"
              data-movement-notice=""
              className="mt-2 text-sm text-content-secondary"
            >
              {movementNotice}
            </p>
          ) : null}
        </section>

        {seesHistory && inUse ? (
          <section aria-labelledby="drawer-history">
            <h2 id="drawer-history" className="mb-2 text-base font-semibold text-content">
              {t('cashDrawer.history.title')}
            </h2>
            <DrawerHistory
              data={sessions}
              error={sessionsError}
              currencySymbol={currencySymbol}
              onPage={setPage}
            />
          </section>
        ) : null}
      </div>

      {closing && current?.session ? (
        <CloseDrawerDialog
          sessionId={current.session.id}
          openingFloatMd={current.session.openingFloatMd}
          currencySymbol={currencySymbol}
          onClose={() => setClosing(false)}
          onClosed={(result) => {
            setClosing(false);
            setClosed(result);
            setPage(1);
            refreshAll();
          }}
          onStale={refreshAll}
        />
      ) : null}

      {closed ? (
        <DrawerClosedDialog
          result={closed}
          currencySymbol={currencySymbol}
          onDone={() => setClosed(null)}
        />
      ) : null}

      {moving ? (
        <DrawerMovementDialog
          kind={moving}
          currencySymbol={currencySymbol}
          onClose={() => setMoving(null)}
          onRecorded={(result) => {
            setMoving(null);
            // The slip numbers ARE the confirmation, as on the safe's ledger.
            setMovementNotice(
              t('cashDrawer.movements.recorded', { numbers: result.voucherNos.join(', ') }),
            );
            refreshAll();
          }}
        />
      ) : null}

      {starting ? (
        <StartCashDrawerDialog
          currencySymbol={currencySymbol}
          onClose={() => setStarting(false)}
          onStarted={() => {
            setStarting(false);
            refreshAll();
          }}
          onAlreadyStarted={refreshAll}
        />
      ) : null}
    </div>
  );
}

/**
 * The drawer as it stands: not in use yet, or its open session with the
 * close. Exported so each state is pinned by render tests.
 */
export function CurrentDrawerCard({
  current,
  error,
  currencySymbol,
  canClose,
  onClose,
  actions,
  onStart,
}: {
  /** Undefined until the first answer. */
  current: DrawerCurrentDto | undefined;
  error: string | null;
  currencySymbol: string;
  /** `bills.create` — whoever takes money at the till may count and close. */
  canClose: boolean;
  onClose: () => void;
  /** Mid-session movements (owner and safe.manage), under the open session. */
  actions?: ReactNode;
  /** The owner's "Start using the cash drawer", while it is not in use. */
  onStart?: () => void;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  // Error before answer: "not in use" is a claim, and a failed read has not
  // established it — pointing at the box screen then would be a guess.
  if (error && current === undefined) {
    return (
      <p role="alert" className="text-sm text-danger">
        {error}
      </p>
    );
  }
  if (current === undefined) {
    return <p className="text-sm text-content-muted">{t('common.loading')}</p>;
  }
  if (!current.enabled) return <DrawerNotInUse onStart={onStart} />;

  const session = current.session;
  if (!session) {
    return (
      <p className="rounded-card border border-subtle bg-raised p-4 text-sm text-content-secondary">
        {t('cashDrawer.current.noSession')}
      </p>
    );
  }

  return (
    <div
      className="rounded-card border border-subtle bg-raised p-4"
      data-drawer-session={session.id}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-1.5 text-xs text-content-secondary">
            <Clock className="h-3.5 w-3.5" aria-hidden />
            {t('cashDrawer.current.openedBy', {
              time: new Date(session.openedAt).toLocaleString(),
              name: session.openedBy.name,
              date: session.businessDate,
            })}
          </p>
          <p className="tabular mt-1 text-xs text-content-secondary">
            {t('cashDrawer.current.openingFloat', { amount: money(session.openingFloatMd) })}
          </p>
          {current.expectedCashMd !== null ? (
            <>
              <p className="mt-2 text-xs text-content-secondary">
                {t('cashDrawer.current.expected')}
              </p>
              <p
                data-expected={current.expectedCashMd}
                className="tabular text-2xl font-bold text-primary [text-shadow:var(--glow-primary)]"
              >
                {money(current.expectedCashMd)}
              </p>
            </>
          ) : null}
          {error ? (
            // A failed refresh keeps the last answer on screen, and says it is not live.
            <p role="alert" className="mt-1 text-xs text-danger">
              {error}
            </p>
          ) : current.expectedCashMd !== null ? (
            <p className="mt-1 text-xs text-content-muted">{t('cashDrawer.current.live')}</p>
          ) : null}
          {current.notCountedSince ? (
            <p
              data-not-counted={current.notCountedSince}
              className="mt-2 flex items-center gap-1.5 text-sm font-medium text-ending"
            >
              <TriangleAlert className="h-4 w-4 shrink-0" aria-hidden />
              {t('cashDrawer.current.notCounted', {
                time: new Date(current.notCountedSince).toLocaleString(),
              })}
            </p>
          ) : null}
        </div>
        {canClose ? (
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover max-sm:w-full"
          >
            {t('cashDrawer.current.close')}
          </button>
        ) : null}
      </div>
      {actions}
    </div>
  );
}

/**
 * Before the owner starts the drawer, the box screen still hands the cash
 * over (switch-over day: the main cashier's normal counted reset comes first).
 */
export function DrawerNotInUse({ onStart }: { onStart?: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      data-drawer-not-in-use=""
      className="flex flex-wrap items-center gap-3 rounded-card border border-subtle bg-raised p-4"
    >
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-content">{t('cashDrawer.notInUse.title')}</p>
        <p className="mt-1 text-xs text-content-muted">{t('cashDrawer.notInUse.body')}</p>
      </div>
      <Link
        to="/box-balance"
        className="rounded-full border border-subtle px-4 py-2 text-center text-sm text-content-secondary hover:bg-hover max-sm:w-full"
      >
        {t('cashDrawer.notInUse.boxBalance')}
      </Link>
      {onStart ? (
        <button
          type="button"
          onClick={onStart}
          className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover max-sm:w-full"
        >
          {t('cashDrawer.start.open')}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The history, one card per session so it reads on a phone without a
 * sideways table. Error before empty, as everywhere money is listed. Only a
 * CLOSED session shows figures: they are the ones stored at its close.
 */
export function DrawerHistory({
  data,
  error,
  currencySymbol,
  onPage,
}: {
  data: DrawerSessionsPage | null;
  error: string | null;
  currencySymbol: string;
  onPage: (page: number) => void;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  if (error) {
    return (
      <p role="alert" className="text-sm text-danger">
        {error}
      </p>
    );
  }
  if (!data) return <p className="text-sm text-content-muted">{t('common.loading')}</p>;
  if (data.rows.length === 0 && data.page <= 1) {
    return <p className="text-sm text-content-muted">{t('cashDrawer.history.empty')}</p>;
  }

  return (
    <>
      <ul className="space-y-2">
        {data.rows.map((session) => (
          <li
            key={session.id}
            data-drawer-session={session.id}
            className="rounded-card border border-subtle bg-raised p-3 text-sm"
          >
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span
                className={[
                  'rounded-full px-2 py-0.5 text-[11px] font-medium',
                  session.status === 'OPEN'
                    ? 'bg-primary/15 text-primary'
                    : 'bg-hover text-content-secondary',
                ].join(' ')}
              >
                {t(`cashDrawer.status.${session.status}`)}
              </span>
              <span className="text-xs text-content-muted">
                {t('cashDrawer.history.businessDate', { date: session.businessDate })}
              </span>
            </div>

            <p className="mt-1 text-xs text-content-secondary">
              {t('cashDrawer.history.opened', {
                time: new Date(session.openedAt).toLocaleString(),
                name: session.openedBy.name,
              })}
            </p>
            {session.closedAt && session.closedBy ? (
              <p className="mt-0.5 text-xs text-content-secondary">
                {t('cashDrawer.history.closed', {
                  time: new Date(session.closedAt).toLocaleString(),
                  name: session.closedBy.name,
                })}
              </p>
            ) : null}

            <dl className="tabular mt-2 grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
              <Figure
                label={t('cashDrawer.figures.openingFloat')}
                value={money(session.openingFloatMd)}
              />
              {session.status === 'CLOSED' && session.expectedCashMd != null ? (
                <Figure
                  label={t('cashDrawer.figures.expected')}
                  value={money(session.expectedCashMd)}
                />
              ) : null}
              {session.status === 'CLOSED' && session.countedCashMd != null ? (
                <Figure
                  label={t('cashDrawer.figures.counted')}
                  value={money(session.countedCashMd)}
                />
              ) : null}
              {session.status === 'CLOSED' && session.varianceMd != null ? (
                <Figure
                  label={t('cashDrawer.figures.variance')}
                  value={signedMoney(session.varianceMd, money)}
                  flagged={BigInt(session.varianceMd) !== 0n}
                />
              ) : null}
              {session.status === 'CLOSED' && session.keptFloatMd != null ? (
                <Figure
                  label={t('cashDrawer.figures.keptFloat')}
                  value={money(session.keptFloatMd)}
                />
              ) : null}
              {session.status === 'CLOSED' && session.droppedToSafeMd != null ? (
                <Figure
                  label={t('cashDrawer.figures.toSafe')}
                  value={money(session.droppedToSafeMd)}
                />
              ) : null}
            </dl>

            {session.closingNote ? (
              <p className="mt-2 text-xs text-content-secondary">
                {t('cashDrawer.history.note')}: {session.closingNote}
              </p>
            ) : null}

            {session.status === 'CLOSED' ? (
              <details className="mt-2">
                <summary className="cursor-pointer text-xs text-content-muted">
                  {t('cashDrawer.history.breakdown')}
                </summary>
                <div className="mt-2">
                  <DrawerFigures session={session} currencySymbol={currencySymbol} />
                </div>
              </details>
            ) : null}
          </li>
        ))}
      </ul>
      <ListPager
        page={data.page}
        hasMore={data.hasMore}
        onPage={onPage}
        previousLabel={t('cashDrawer.history.previousPage')}
        nextLabel={t('cashDrawer.history.nextPage')}
      />
    </>
  );
}

/**
 * Read back after a close: the figures as stored — what this close reads as
 * from now on — the safe slip the drop was written on, and the float the
 * next session started with.
 */
export function DrawerClosedDialog({
  result,
  currencySymbol,
  onDone,
}: {
  result: DrawerCloseResultDto;
  currencySymbol: string;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={t('cashDrawer.done.title')}
        className="max-h-full w-full max-w-md overflow-auto rounded-card border border-subtle bg-ground p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{t('cashDrawer.done.title')}</h2>
        <div className="mt-3">
          <DrawerFigures session={result.closed} currencySymbol={currencySymbol} />
        </div>
        {result.voucherNo !== null ? (
          <p data-voucher={result.voucherNo} className="mt-3 text-sm text-content">
            {t('cashDrawer.done.slip', { number: result.voucherNo })}
          </p>
        ) : null}
        <p className="tabular mt-1 text-sm text-content-secondary">
          {t('cashDrawer.done.next', { amount: money(result.next.openingFloatMd) })}
        </p>
        <p className="mt-1 text-xs text-content-muted">{t('cashDrawer.done.printed')}</p>
        <button
          type="button"
          onClick={onDone}
          className="mt-4 w-full rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-fg hover:bg-primary-hover"
        >
          {t('common.confirm')}
        </button>
      </div>
    </div>
  );
}

function Figure({
  label,
  value,
  flagged = false,
}: {
  label: string;
  value: string;
  flagged?: boolean;
}) {
  return (
    <div className="flex flex-col">
      <dt className="text-[11px] text-content-muted">{label}</dt>
      <dd className={flagged ? 'font-medium text-ending' : 'text-content'}>{value}</dd>
    </div>
  );
}
